import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import CloseIcon from '@mui/icons-material/Close'

type Props = {
  isOpen: boolean
  setIsOpen: (value: boolean) => void
  children: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
}
const Modal = ({ isOpen, setIsOpen, children, size = 'md' }: Props) => {
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  }

  return (
    <Dialog
      as={'div'}
      open={isOpen}
      onClose={() => setIsOpen(false)}
      transition
      className="fixed inset-0 flex w-screen items-center justify-center bg-black/30 p-4 pt-16 transition duration-300 ease-out data-closed:opacity-0"
    >
      <DialogPanel
        className={`w-full ${sizeClasses[size]} mx-4 max-h-[calc(100vh-8rem)] transform bg-white 
            text-left align-middle shadow-xl transition-all flex flex-col rounded-2xl overflow-hidden`}
      >
        {children}
      </DialogPanel>
    </Dialog>
  )
}

type HeaderProps = {
  children: React.ReactNode
  onClose: () => void
}
const Header = ({ onClose, children }: HeaderProps) => {
  return (
    <div className="flex justify-between overflow-hidden px-6 pt-6 pb-4">
      <div className="flex items-center overflow-hidden">
        <DialogTitle
          as="h3"
          className="text-lg font-medium leading-6 text-gray-900"
        >
          {children}
        </DialogTitle>
      </div>

      <button
        aria-label="Close modal"
        className="flex items-center transition ease-in-out delay-100 hover:scale-110 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 rounded-md p-1"
        onClick={onClose}
      >
        <CloseIcon sx={{ fontSize: 20 }} />
      </button>
    </div>
  )
}

type BodyProps = {
  children: React.ReactNode
}
const Body = ({ children }: BodyProps) => {
  return (
    <div className="px-6 py-4 flex-1 overflow-auto min-h-0">{children}</div>
  )
}

type FooterProps = {
  children: React.ReactNode
}
const Footer = ({ children }: FooterProps) => {
  return (
    <div className="px-6 pb-6 pt-2 flex flex-row-reverse justify-start gap-3">
      {children}
    </div>
  )
}

const Separator = () => {
  return <hr className="h-px mx-6 bg-gray-100 border-0" />
}

Modal.Header = Header
Modal.Body = Body
Modal.Footer = Footer
Modal.Separator = Separator

export default Modal
